import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

// ─── SectionCard ──────────────────────────────────────────────────────────────

export function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs">{children}</div>
    </div>
  );
}

// ─── SectionTitle ─────────────────────────────────────────────────────────────

export function SectionTitle({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-nano">
      <div className="flex items-center gap-mini">
        {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
        <div className="flex flex-col">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
          {description && (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

// ─── DocumentProgressRing ─────────────────────────────────────────────────────

export function DocumentProgressRing({
  approved,
  total,
  pending,
  rejected,
  notSent,
}: {
  approved: number;
  total: number;
  pending: number;
  rejected: number;
  notSent?: number;
}) {
  const { t } = useTranslation('personManagement');
  const percent = total > 0 ? Math.round((approved / total) * 100) : 0;
  const r = 38;
  const circumference = 2 * Math.PI * r;

  // Chips de status: aprovado sempre visível (métrica principal); os demais
  // aparecem apenas quando há contagem, evitando ruído com "0 ...".
  const chips = [
    {
      key: 'approved',
      value: approved,
      className: 'bg-base-success-100 text-base-success-700',
      label: t('enrollmentDocumentation.approvedCount', { count: approved }),
    },
    {
      key: 'pending',
      value: pending,
      className: 'bg-base-warning-100 text-base-warning-700',
      label: t('enrollmentDocumentation.pendingCount', { count: pending }),
    },
    {
      key: 'rejected',
      value: rejected,
      className: 'bg-base-danger-100 text-base-danger-700',
      label: t('enrollmentDocumentation.rejectedCount', { count: rejected }),
    },
    {
      key: 'notSent',
      value: notSent ?? 0,
      className: 'bg-ctx-layout-spotlight text-ctx-content-placeholder',
      label: t('enrollmentDocumentation.notSentCount', { count: notSent ?? 0 }),
    },
  ].filter(chip => chip.key === 'approved' || chip.value > 0);

  // Segmentos coloridos do anel (aprovado · rejeitado · pendente). O restante
  // (não enviados) permanece como a trilha cinza de fundo. Mesmo com 0% aprovado,
  // o anel comunica visualmente o estado dos documentos.
  const segments = [
    { value: approved, className: 'stroke-base-success-500' },
    { value: rejected, className: 'stroke-base-danger-500' },
    { value: pending, className: 'stroke-base-warning-500' },
  ].filter(seg => seg.value > 0);

  let segmentOffset = 0;

  return (
    <div className="flex items-center gap-xxs">
      <div className="relative flex h-[4.5rem] w-[4.5rem] shrink-0 items-center justify-center">
        <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            className="stroke-ctx-layout-body text-ctx-layout-border"
            strokeWidth="10"
          />
          {total > 0 &&
            segments.map(seg => {
              const length = (seg.value / total) * circumference;
              const dashOffset = -segmentOffset;
              segmentOffset += length;
              return (
                <circle
                  key={seg.className}
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  className={seg.className}
                  strokeWidth="10"
                  strokeDasharray={`${length} ${circumference}`}
                  strokeDashoffset={dashOffset}
                />
              );
            })}
        </svg>
        <span className="absolute ts-caption-xxs !font-bold text-ctx-content-title">
          {percent}%
        </span>
      </div>
      <div className="flex flex-col gap-xxxs">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
          {t('enrollmentDocumentation.approvedCountLabel', {
            count: approved,
            total,
          })}
        </span>
        <div className="flex flex-wrap items-center gap-quark">
          {chips.map(chip => (
            <span
              key={chip.key}
              className={cn(
                'rounded-pill px-mini py-quark ts-caption-xxs !font-medium',
                chip.className,
              )}
            >
              {chip.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── DocumentRejectedAlert ────────────────────────────────────────────────────

export function DocumentRejectedAlert({ docType, reason }: { docType: string; reason: string }) {
  const { t } = useTranslation('personManagement');
  return (
    <div className="flex items-start gap-xxs rounded-sm border-hairline border-base-warning-300 bg-base-warning-100 p-xxs">
      <ExclamationTriangleIcon className="mt-quark h-xxxs w-xxxs shrink-0 text-base-warning-600" />
      <div className="flex flex-col gap-quark">
        <span className="ts-paragraph-xs !font-medium text-base-brand-900">
          {t('enrollmentDocumentation.rejectedAlertTitle', { docType })}
        </span>
        <span className="ts-caption-xxs text-base-brand-600">
          {reason
            ? t('enrollmentDocumentation.rejectedAlertDesc', { reason })
            : t('enrollmentDocumentation.rejectedAlertDescNoReason')}
        </span>
      </div>
    </div>
  );
}

// ─── DocumentRejectedBanner ───────────────────────────────────────────────────

/** Documento rejeitado exibido como item do banner consolidado. */
export interface RejectedDocSummary {
  readonly id: string | number;
  readonly docType: string;
  /** Motivo da rejeição; vazio quando nenhum motivo foi registrado. */
  readonly reason: string;
}

/**
 * Banner único consolidado dos documentos rejeitados de uma matrícula.
 *
 * Em vez de um card por documento, agrupa todos os rejeitados em um só alerta
 * com a contagem no título e uma lista compacta (tipo do documento + motivo).
 * Retorna `null` quando não há documentos rejeitados.
 *
 * @param docs - Lista de documentos rejeitados a exibir
 */
export function DocumentRejectedBanner({ docs }: { docs: readonly RejectedDocSummary[] }) {
  const { t } = useTranslation('personManagement');

  if (docs.length === 0) return null;

  return (
    <div className="flex items-start gap-xxs rounded-sm border-hairline border-base-warning-300 bg-base-warning-100 p-xxs">
      <ExclamationTriangleIcon className="mt-quark h-xxxs w-xxxs shrink-0 text-base-warning-600" />
      <div className="flex min-w-none flex-col gap-xxxs">
        <span className="ts-paragraph-xs !font-medium text-base-brand-900">
          {t('enrollmentDocumentation.rejectedBannerTitle', {
            count: docs.length,
          })}
        </span>
        <ul className="flex flex-col gap-quark">
          {docs.map(doc => (
            <li key={doc.id} className="ts-caption-xxs text-base-brand-600">
              <span className="!font-medium text-base-brand-900">{doc.docType}</span>
              {' — '}
              {doc.reason ? doc.reason : t('enrollmentDocumentation.rejectedBannerNoReason')}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
